/* Tri-color (4-state) segmented enrichment progress bar with per-segment
 * hover tooltips. Tooltip renders via a fixed-position portal so it never
 * gets clipped by the table's overflow container. */
import React, { useState } from "react";
import { createPortal } from "react-dom";

const SEGMENT_STYLES = {
  green: "bg-emerald-500",
  amber: "bg-amber-400",
  red: "bg-red-500",
  grey: "bg-slate-200",
};
const SEGMENT_TITLES = {
  green: "Found",
  amber: "Pending review",
  red: "Searched — nothing found",
  grey: "Not searched yet",
};
const ORDER = ["green", "amber", "red", "grey"];

export default function EnrichmentBar({ progress, realtorId, showCount = true }) {
  const [tip, setTip] = useState(null);
  const { points, counts, total, isEnriching } = progress;

  const show = (state) => (e) => {
    const items = points.filter((p) => p.state === state);
    if (!items.length) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setTip({ state, items, x: rect.left + rect.width / 2, y: rect.top });
  };
  const hide = () => setTip(null);

  return (
    <div className="flex items-center gap-2" data-testid={`enrichment-bar-${realtorId}`}>
      <div
        className={`flex h-2 w-24 min-w-24 overflow-hidden rounded-full ring-1 ring-slate-200 ${
          isEnriching ? "animate-pulse" : ""
        }`}
      >
        {ORDER.map((state) =>
          counts[state] > 0 ? (
            <div
              key={state}
              className={`${SEGMENT_STYLES[state]} h-full cursor-help transition-[width] duration-500`}
              style={{ width: `${(counts[state] / total) * 100}%` }}
              onMouseEnter={show(state)}
              onMouseLeave={hide}
              data-testid={`enrichment-segment-${state}-${realtorId}`}
            />
          ) : null,
        )}
      </div>
      {showCount ? (
        <span
          className="text-[10px] tabular-nums text-slate-500 whitespace-nowrap"
          data-testid={`enrichment-count-${realtorId}`}
        >
          {counts.green}/{total}
        </span>
      ) : null}
      {tip
        ? createPortal(
            <div
              className="fixed z-[999] -translate-x-1/2 -translate-y-full pb-2 pointer-events-none"
              style={{ left: tip.x, top: tip.y }}
              data-testid="enrichment-tooltip"
            >
              <div className="rounded-md bg-slate-900 text-white text-xs shadow-lg px-3 py-2 max-w-[280px]">
                <div className="font-semibold mb-1 flex items-center gap-1.5">
                  <span className={`inline-block h-2 w-2 rounded-full ${SEGMENT_STYLES[tip.state]}`} />
                  {SEGMENT_TITLES[tip.state]} ({tip.items.length})
                </div>
                <ul className="space-y-0.5">
                  {tip.items.map((p) => (
                    <li key={p.field}>
                      <span className="font-medium">{p.label}</span>{" "}
                      <span className="text-slate-300">— {p.detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
